import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
import { AgentMark } from '../ui/AgentMark';
import { LevelBadge } from '../ui/LevelBadge';
import { ProcessingTag } from '../ui/ProcessingTag';
import { WhyPopover } from '../ui/WhyPopover';
import { SlideToConfirm } from '../ui/SlideToConfirm';
import { RollingCounter } from '../ui/RollingCounter';
import { useDb } from '../../hooks/useDb';
import { db } from '../../utils/db';
import { pendingProposals, proposalLines } from '../../utils/dbSelectors';
import type { DeskStage } from '../../hooks/useDeskStage';
import type { Proposal } from '../../types/db';
const URGENCY: Record<Proposal['urgency'], string> = {
  nu: 'Nu',
  vandaag: 'Vandaag',
  'kan-wachten': 'Kan wachten'
};
const SLIDE: Record<Proposal['kind'], [string, string]> = {
  mail: ['Schuif om te versturen', 'Verstuurd'],
  code: ['Schuif om goed te keuren', 'Goedgekeurd'],
  boekhouding: ['Schuif om in te boeken', 'Ingeboekt']
};
function WaitingRow({
  p,
  stage
}: {
  p: Proposal;
  stage: DeskStage;
}) {
  const [open, setOpen] = useState(false);
  const lines = useDb(t => proposalLines(t, p), [p]);
  return <div role="listitem" className="rounded-2xl border border-white/[0.07] bg-night-900/70" data-id="element-830" data-mp="2vg4s15hcdp41">
      <button type="button" aria-expanded={open} onClick={() => setOpen(v => !v)} className="flex w-full items-center gap-3 px-3 py-2.5 text-left" data-id="element-831" data-mp="2qgifyzd92ewt">
        <AgentMark agentId={p.agent} size="sm" data-id="element-832" data-mp="30fhf9rc4ky75" />
        <span className="min-w-0 flex-1 truncate text-[15px] text-ink" data-id="element-833" data-mp="30ephqayywqe8">{p.title}</span>
        <span className={`text-[13px] ${p.urgency === 'nu' ? 'text-amber' : 'text-ink-muted'}`} data-id="element-834" data-mp="3e52t6nb2in8b">{URGENCY[p.urgency]}</span>
        <ChevronDownIcon className={`h-4 w-4 text-ink-muted transition-transform duration-150 ${open ? 'rotate-180' : ''}`} aria-hidden data-id="element-835" data-mp="3a6vkmfqrcs87" />
      </button>
      <AnimatePresence initial={false} data-id="element-836" data-mp="3j68mam8n4cid">
        {open && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="overflow-hidden" data-id="element-837" data-mp="3j3mfwpxhk31q">
            <div className="px-3 pb-3" data-id="element-838" data-mp="2pqwfcrvdbmzj">
              <ul className="space-y-0.5 text-[14px] text-ink-soft" data-id="element-839" data-mp="2ujpjbdj0irak">
                {lines.map(l => <li key={l} className="line-clamp-2" data-id="element-840" data-mp="2zhhb87jnqder">{l}</li>)}
                {p.kind === 'code' && <li className="text-ink" data-id="element-841" data-mp="34gkilyn40vpw">Mergen doe je daarna zelf.</li>}
              </ul>
              <div className="mt-2 flex flex-wrap items-center gap-2" data-id="element-842" data-mp="38iz0ogiyfrg4">
                <LevelBadge level="naOk" data-id="element-843" data-mp="3di1uf4thib8s" />
                <ProcessingTag where={p.where} data-id="element-844" data-mp="3dh0k52kalss4" />
                <span className="ml-auto" data-id="element-845" data-mp="1e57j8uudw3a4">
                  <WhyPopover traceId={p.traceId} data-id="element-846" data-mp="1e6939otdo03z" />
                </span>
              </div>
              <div className="mt-3" data-id="element-847" data-mp="1j5c0in34921e">
                <SlideToConfirm label={SLIDE[p.kind][0]} confirmedLabel={SLIDE[p.kind][1]} onConfirm={() => {
              window.setTimeout(() => db.approveProposal(p.id), 450);
              stage.approved(p.agent, `${p.title} · ${SLIDE[p.kind][1].toLowerCase()}`);
            }} data-id="element-848" data-mp="1j6dcquvvbukp" />
              </div>
              <div className="mt-2 flex gap-2" data-id="element-849" data-mp="wxopgm8xjkxd">
                <button type="button" onClick={() => stage.showToast('Aanpassen opent het concept op het podium.', 'meldt')} className="h-10 flex-1 whitespace-nowrap rounded-full border border-white/10 text-[14px] text-ink-soft transition-colors duration-150 hover:border-white/20 hover:text-ink" data-id="element-850" data-mp="1uk5ktly3rogh">
                  Aanpassen
                </button>
                <button type="button" onClick={() => {
              db.rejectProposal(p.id);
              stage.showToast('Afgewezen. Anna leert hiervan.', 'geweigerd');
            }} className="h-10 flex-1 whitespace-nowrap rounded-full text-[14px] text-ink-muted transition-colors duration-150 hover:bg-white/[0.05] hover:text-ink" data-id="element-851" data-mp="97cc2vux0vze">
                  Afwijzen
                </button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}

/** Bottom-right: Niveau 3 proposals, each expandable. */
export function WaitingStack({
  stage
}: {
  stage: DeskStage;
}) {
  const pending = useDb(pendingProposals);
  return <section aria-label="Wacht op jou" className="flex min-h-0 flex-col border-t border-white/[0.06] pt-4" data-id="element-852" data-mp="22rgrlf1fl6xz">
      <div className="flex items-center justify-between px-3" data-id="element-853" data-mp="26s91mai4nfvb">
        <h2 className="text-[16px] font-semibold text-ink" data-id="element-854" data-mp="26uv806ta7pby">Wacht op jou</h2>
        <span className="grid h-7 min-w-[28px] place-items-center rounded-full bg-white/[0.08] px-2 text-[14px] font-semibold text-ink" data-id="element-855" data-mp="ragqpv10pjsc">
          <RollingCounter value={pending.length} data-id="element-856" data-mp="nu43u3l6a1ci" />
        </span>
      </div>
      {pending.length === 0 ? <p className="px-3 pt-3 text-[14px] text-ink-muted" data-id="element-857" data-mp="iv9lf2r4aesx">Niets wacht op je.</p> : <div role="list" className="no-scrollbar mt-3 min-h-0 space-y-2 overflow-y-auto" data-id="element-858" data-mp="xsa6woiptdiy">
          <AnimatePresence initial={false} data-id="element-859" data-mp="st7afsgswfml">
            {pending.map(p => <motion.div key={p.id} layout exit={{
          opacity: 0,
          x: -40,
          transition: {
            duration: 0.2
          }
        }} data-id="element-860" data-mp="sr49ide41233">
                <WaitingRow p={p} stage={stage} data-id="element-861" data-mp="519ym1dcpnlh" />
              </motion.div>)}
          </AnimatePresence>
        </div>}
    </section>;
}